import React, { useState } from 'react';
import { CheckIcon, PencilIcon, Trash2Icon, XIcon } from 'lucide-react';
import { ProJob, TimeEntry, WorkType } from '../../types/pro';
import { formatDate } from '../../utils/pro';
import { EmptyState } from './EmptyState';
const WORK_TYPES: WorkType[] = ['Montage', 'Fejlfinding', 'Service', 'Kørsel', 'Andet'];
interface TimeEntryListProps {
  entries: TimeEntry[];
  /** Used to name the job on each row; omit when the list is inside a job. */
  jobs?: ProJob[];
  emptyTitle: string;
  onUpdate: (id: string, changes: Partial<TimeEntry>) => void;
  onRemove: (id: string) => void;
}
interface Draft {
  date: string;
  hours: string;
  workType: WorkType;
  note: string;
  billable: boolean;
}

/**
 * Registered hours, editable in place. Mistyping 8 hours instead of 0.8 is
 * ordinary, and it flows straight into the invoice, so correcting a row has
 * to be as easy as writing it.
 */
export function TimeEntryList({
  entries,
  jobs,
  emptyTitle,
  onUpdate,
  onRemove
}: TimeEntryListProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const beginEdit = (entry: TimeEntry) => {
    setEditingId(entry.id ?? null);
    setDraft({
      date: entry.date,
      hours: String(entry.hours).replace('.', ','),
      workType: entry.workType,
      note: entry.note,
      billable: entry.billable
    });
  };
  const cancel = () => {
    setEditingId(null);
    setDraft(null);
  };
  const save = () => {
    if (!editingId || !draft) return;
    const hours = parseFloat(draft.hours.replace(',', '.'));
    if (!hours || hours <= 0) return;
    onUpdate(editingId, {
      date: draft.date,
      hours,
      workType: draft.workType,
      note: draft.note.trim(),
      billable: draft.billable
    });
    cancel();
  };
  const field = 'w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-transparent focus:ring-2 focus:ring-brand-500';
  if (entries.length === 0) return <EmptyState title={emptyTitle} data-id="element-204" data-mp="3gdrjferuw0jj" />;
  return <ul className="divide-y divide-stone-100" data-id="element-205" data-mp="3gh6wyd5vt1iu">
      {entries.map(entry => {
      const job = jobs?.find(item => item.id === entry.jobId);
      const isEditing = editingId === entry.id;
      if (isEditing && draft) {
        return <li key={entry.id} className="bg-brand-50/40 px-5 py-4" data-id="element-206" data-mp="3udapm213x1t7">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-[150px_100px_1fr]" data-id="element-207" data-mp="2xouy40efsidg">
                <label className="block" data-id="element-208" data-mp="32n68qdck2wwq">
                  <span className="mb-1 block text-xs font-medium text-stone-500" data-id="element-209" data-mp="32o7m4oh1nvhm">
                    Dato
                  </span>
                  <input type="date" value={draft.date} onChange={e => setDraft({
                ...draft,
                date: e.target.value
              })} className={field} data-id="element-210" data-mp="36mwxkbiiv2ym" />
                </label>
                <label className="block" data-id="element-211" data-mp="2ix2hzm6tj0ao">
                  <span className="mb-1 block text-xs font-medium text-stone-500" data-id="element-212" data-mp="2bg61h15lywdg">
                    Timer
                  </span>
                  <input autoFocus value={draft.hours} onChange={e => setDraft({
                ...draft,
                hours: e.target.value
              })} inputMode="decimal" className={`${field} tabular-nums`} data-id="element-213" data-mp="26hmcsszmr6he" />
                </label>
                <label className="col-span-2 block sm:col-span-1" data-id="element-214" data-mp="1nq3a6n9ipufw">
                  <span className="mb-1 block text-xs font-medium text-stone-500" data-id="element-215" data-mp="1ir8j6y1eczkp">
                    Arbejdstype
                  </span>
                  <select value={draft.workType} onChange={e => setDraft({
                ...draft,
                workType: e.target.value as WorkType
              })} className={field} data-id="element-216" data-mp="1eqon7xsk8205">
                    {WORK_TYPES.map(type => <option key={type} value={type} data-id="element-217" data-mp="39t37vmbdbn05">
                        {type}
                      </option>)}
                  </select>
                </label>
              </div>

              <label className="mt-3 block" data-id="element-218" data-mp="301yswmlp025b">
                <span className="mb-1 block text-xs font-medium text-stone-500" data-id="element-219" data-mp="300nockkfaqd5">
                  Note
                </span>
                <textarea value={draft.note} onChange={e => setDraft({
              ...draft,
              note: e.target.value
            })} rows={2} placeholder="Hvad blev der lavet?" className={`${field} leading-relaxed`} data-id="element-220" data-mp="2q4kkna9xajo3" />
              </label>

              <div className="mt-3 flex flex-wrap items-center gap-3" data-id="element-221" data-mp="aeq0zd0129m9">
                <label className="flex items-center gap-2 text-sm text-stone-700" data-id="element-222" data-mp="fcj3nw24glhs">
                  <input type="checkbox" checked={draft.billable} onChange={e => setDraft({
                ...draft,
                billable: e.target.checked
              })} className="h-4 w-4 accent-brand-600" data-id="element-223" data-mp="faf0xhfylsj3" />
                  Kan faktureres
                </label>
                <span className="flex-1" data-id="element-224" data-mp="24slqffxmjm3i" />
                <button onClick={cancel} className="inline-flex items-center gap-1.5 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-stone-50" data-id="element-225" data-mp="2il1o017rarx1">
                  <XIcon className="h-4 w-4" data-id="element-226" data-mp="2shepvmdggc2v" />
                  Annuller
                </button>
                <button onClick={save} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-227" data-mp="2nibkg0tkc3ud">
                  <CheckIcon className="h-4 w-4" data-id="element-228" data-mp="1iv0tmbuk80t5" />
                  Gem
                </button>
              </div>
            </li>;
      }
      return <li key={entry.id} className="flex items-center gap-3 px-5 py-3" data-id="element-229" data-mp="1mxxgier4uj2w">
            <span className="min-w-0 flex-1" data-id="element-230" data-mp="1rtw2jvoznp05">
              <span className="block truncate text-sm font-medium text-stone-900" data-id="element-231" data-mp="1wutie1mj06yh">
                {jobs ? job?.title ?? 'Slettet opgave' : entry.workType}
              </span>
              <span className="block truncate text-xs text-stone-500" data-id="element-232" data-mp="25uyqkdzjn9dp">
                {jobs && `${entry.workType} · `}
                {formatDate(entry.date)}
                {entry.note && ` · ${entry.note}`}
                {!entry.billable && ' · ikke fakturerbar'}
              </span>
            </span>
            <span className="flex-shrink-0 text-sm font-semibold tabular-nums text-stone-900" data-id="element-233" data-mp="2kk4lhprcmdfs">
              {entry.hours.toLocaleString('da-DK')} t
            </span>
            <button onClick={() => beginEdit(entry)} aria-label="Ret registrering" className="flex-shrink-0 rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" data-id="element-234" data-mp="2pgv2cfaz9d29">
              <PencilIcon className="h-4 w-4" data-id="element-235" data-mp="3wmxj3oo6mtn" />
            </button>
            <button onClick={() => entry.id && onRemove(entry.id)} aria-label="Slet registrering" className="flex-shrink-0 rounded-lg p-1.5 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-236" data-mp="3v3r27rxcdwyw">
              <Trash2Icon className="h-4 w-4" data-id="element-237" data-mp="rnsjm0l7ebce" />
            </button>
          </li>;
    })}
    </ul>;
}